import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Pencil, BookOpen, FileText, Upload, Brain, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  'Platform Guide',
  'Process',
  'Policy',
  'FAQ',
  'Training',
  'Onboarding',
  'General',
];

export function AdminKeliTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({ title: '', category: 'General', content: '' });
  const [uploading, setUploading] = useState(false);

  const token = sessionStorage.getItem('session_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  const loadEntries = () => {
    fetch('/api/keli/knowledge', { credentials: 'include', headers })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setEntries(data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadEntries(); }, []);

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setUploading(true);
    try {
      const url = editEntry ? `/api/keli/knowledge/${editEntry.knowledge_id}` : '/api/keli/knowledge';
      const method = editEntry ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, credentials: 'include', headers,
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to save');
      toast.success(editEntry ? 'Knowledge updated' : 'Knowledge added');
      setShowAdd(false);
      setEditEntry(null);
      setForm({ title: '', category: 'General', content: '' });
      loadEntries();
    } catch {
      toast.error('Failed to save knowledge entry');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await window.appConfirm('Delete this knowledge entry?'))) return;
    try {
      await fetch(`/api/keli/knowledge/${id}`, { method: 'DELETE', credentials: 'include', headers });
      toast.success('Deleted');
      loadEntries();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      setForm(prev => ({
        ...prev,
        content: prev.content ? prev.content + '\n\n' + text : text,
        title: prev.title || file.name.replace(/\.[^.]+$/, ''),
      }));
      toast.success(`File "${file.name}" loaded`);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const openEdit = (entry) => {
    setEditEntry(entry);
    setForm({ title: entry.title, category: entry.category, content: entry.content });
    setShowAdd(true);
  };

  const openAdd = () => {
    setEditEntry(null);
    setForm({ title: '', category: 'General', content: '' });
    setShowAdd(true);
  };

  const [ingesting, setIngesting] = useState(false);

  const handleIngestDocs = async () => {
    if (!(await window.appConfirm('Re-import all platform documentation from /app/docs into Keli? This replaces existing doc-sourced entries (admin-authored entries are not touched).'))) return;
    setIngesting(true);
    try {
      const res = await fetch('/api/keli/knowledge/ingest-docs', {
        method: 'POST', credentials: 'include', headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(`Ingested ${data.entries_added} chunks from ${data.files_processed} doc(s) (${data.entries_replaced} old entries replaced).`);
      loadEntries();
    } catch (e) { toast.error(e.message || 'Failed'); }
    setIngesting(false);
  };

  const totalChars = entries.reduce((sum, e) => sum + (e.content?.length || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Brain className="h-6 w-6 text-brand-navy" />
          <div>
            <h2 className="text-xl font-bold font-heading">Keli Knowledge Base</h2>
            <p className="text-sm text-slate-500">
              {entries.length} document{entries.length !== 1 ? 's' : ''} indexed &middot; {(totalChars / 1000).toFixed(1)}k characters
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="ingest-docs-btn" onClick={handleIngestDocs} disabled={ingesting} variant="outline" className="border-brand-navy/30 text-brand-navy hover:bg-brand-navy/5">
            <FileText className="h-4 w-4 mr-2" /> {ingesting ? 'Ingesting…' : 'Sync Platform Docs'}
          </Button>
          <Button data-testid="add-knowledge-btn" onClick={openAdd} className="bg-brand-navy hover:bg-brand-dark text-white">
            <Plus className="h-4 w-4 mr-2" /> Add Knowledge
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="p-4 bg-brand-navy/5 border-brand-navy/10">
        <p className="text-sm text-slate-600">
          Everything uploaded here becomes part of Keli's knowledge. She will reference these documents when answering member questions about the platform, processes, and policies. Upload guides, FAQs, process documents, or paste text directly.
        </p>
      </Card>

      {/* Entries List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-navy mx-auto" />
        </div>
      ) : entries.length === 0 ? (
        <Card className="p-12 text-center">
          <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No knowledge documents yet. Add your first one to train Keli.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {entries.map(entry => (
            <Card key={entry.knowledge_id} data-testid={`knowledge-entry-${entry.knowledge_id}`} className="p-4 border border-slate-200 hover:border-slate-300 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="h-4 w-4 text-brand-navy flex-shrink-0" />
                    <h3 className="font-semibold text-slate-800 truncate">{entry.title}</h3>
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-brand-navy/10 text-brand-navy flex-shrink-0">
                      {entry.category}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">
                    {(entry.content?.length || 0).toLocaleString()} chars &middot; Added {new Date(entry.created_at).toLocaleDateString()}
                    {entry.updated_at && entry.updated_at !== entry.created_at && ` &middot; Updated ${new Date(entry.updated_at).toLocaleDateString()}`}
                  </p>

                  {/* Expandable content preview */}
                  <button
                    onClick={() => setExpandedId(expandedId === entry.knowledge_id ? null : entry.knowledge_id)}
                    className="flex items-center gap-1 text-xs text-brand-navy hover:text-brand-dark mt-2 transition-colors"
                    data-testid={`toggle-preview-${entry.knowledge_id}`}
                  >
                    {expandedId === entry.knowledge_id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    {expandedId === entry.knowledge_id ? 'Hide preview' : 'Show preview'}
                  </button>
                  {expandedId === entry.knowledge_id && (
                    <pre className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600 max-h-64 overflow-y-auto whitespace-pre-wrap font-sans">
                      {entry.content?.slice(0, 5000)}{entry.content?.length > 5000 ? '\n\n... (truncated)' : ''}
                    </pre>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button data-testid={`edit-knowledge-${entry.knowledge_id}`} variant="ghost" size="sm" onClick={() => openEdit(entry)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button data-testid={`delete-knowledge-${entry.knowledge_id}`} variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(entry.knowledge_id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showAdd} onOpenChange={(open) => { if (!open) { setShowAdd(false); setEditEntry(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="knowledge-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editEntry ? 'Edit Knowledge Entry' : 'Add Knowledge'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Title</label>
              <Input
                data-testid="knowledge-title-input"
                placeholder="e.g. Platform User Manual, Onboarding Process"
                value={form.title}
                onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Category</label>
              <Select value={form.category} onValueChange={v => setForm(prev => ({ ...prev, category: v }))}>
                <SelectTrigger data-testid="knowledge-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-slate-700">Content</label>
                <label className="cursor-pointer">
                  <input type="file" accept=".txt,.md,.csv,.json" className="hidden" onChange={handleFileUpload} />
                  <span className="flex items-center gap-1 text-xs text-brand-navy hover:text-brand-dark transition-colors" data-testid="upload-file-btn">
                    <Upload className="h-3 w-3" /> Import from file
                  </span>
                </label>
              </div>
              <Textarea
                data-testid="knowledge-content-input"
                placeholder="Paste or type knowledge content here. This can be a full document, FAQ answers, process descriptions, policies, or any reference material Keli should know about."
                value={form.content}
                onChange={e => setForm(prev => ({ ...prev, content: e.target.value }))}
                className="min-h-[300px] font-mono text-sm"
              />
              <p className="text-xs text-slate-400 mt-1">{(form.content?.length || 0).toLocaleString()} characters</p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setShowAdd(false); setEditEntry(null); }}>Cancel</Button>
              <Button
                data-testid="save-knowledge-btn"
                onClick={handleSubmit}
                disabled={uploading || !form.title.trim() || !form.content.trim()}
                className="bg-brand-navy hover:bg-brand-dark text-white"
              >
                {uploading ? 'Saving...' : editEntry ? 'Update' : 'Add to Knowledge Base'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
